import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PorterCalculator, { calcPorterage } from '../components/PorterCalculator';

export default function OrderPage() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [last, setLast] = useState(null);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [floor, setFloor] = useState('');
  const [elevator, setElevator] = useState(false);

  useEffect(() => {
    axios.get(`/api/public/order/${token}`)
      .then(({ data }) => {
        setOrder(data.order);
        setLast(data.last_update);
        if (data.last_update?.requested_date) setDate(String(data.last_update.requested_date).slice(0, 10));
        if (data.last_update?.notes) setNotes(data.last_update.notes);
        if (data.last_update?.floor) setFloor(data.last_update.floor);
        if (data.last_update?.porterage_json?.elevator) setElevator(true);
      })
      .catch((err) => setError(err.response?.data?.error || 'הקישור אינו תקף'));
  }, [token]);

  const porterage = calcPorterage({
    weight: order?.weight,
    floors: floor,
    elevator,
    stairsRate: order?.stairs_rate,
    elevatorRate: order?.elevator_rate,
  });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setOk('');
    setBusy(true);
    try {
      await axios.post(`/api/public/order/${token}`, {
        requested_date: date || null,
        notes,
        floor,
        porterage,
        porterage_total: porterage.total,
      });
      setOk('העדכון נשמר. צוות ההפצה יראה אותו.');
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setBusy(false);
    }
  };

  if (error && !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-md text-center text-red-600">{error}</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nexolog-teal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-nexolog-navy text-white py-4 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-2xl font-bold"><span style={{ color: '#1DBFAF' }}>Nexolog</span></div>
          <div className="text-sm text-gray-300">עדכון משלוח</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <section className="bg-white rounded-xl shadow p-4 space-y-1">
          <h1 className="text-xl font-bold text-nexolog-navy">הזמנה {order.order_id}</h1>
          {order.demo && (
            <div className="mb-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2">
              זה דף הדגמה לבדיקת SMS. הזמנות אמיתיות יקבלו קישור אישי עם פרטי המשלוח.
            </div>
          )}
          {order.company && <p className="text-sm text-gray-600">{order.company}</p>}
          <p className="text-sm">נמען: {order.recipient || '—'}</p>
          <p className="text-sm">כתובת: {order.address || '—'}</p>
          <p className="text-sm">תאריך מתוכנן: {order.pickup_at_fmt}</p>
          <p className="text-sm">סטטוס: {order.status}</p>
        </section>

        {last && (
          <div className="bg-teal-50 border border-teal-200 text-teal-800 text-sm rounded-xl p-3">
            עדכון אחרון נשמר. אפשר לשלוח עדכון חדש אם צריך.
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{error}</div>}
        {ok && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl p-3">{ok}</div>}

        <form onSubmit={submit} className="space-y-4">
          <section className="bg-white rounded-xl shadow p-4 space-y-3">
            <h2 className="font-bold text-nexolog-navy">שינוי תאריך קבלה</h2>
            <input
              type="date"
              dir="ltr"
              min={order.min_date}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg min-h-[42px] px-3 text-left"
            />
            <label className="block">
              <span className="text-sm font-semibold">מספר קומה</span>
              <input
                value={floor}
                onChange={(e) => setFloor(e.target.value)}
                placeholder="לדוגמה: 3"
                className="mt-1 w-full border border-gray-300 rounded-lg min-h-[42px] px-3"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">הערות לשליח / שליחת עדכון</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="שעות נוחות, קוד כניסה, כלב בחצר..."
              />
            </label>
          </section>

          {order.calculator_enabled && (
            <PorterCalculator
              weight={order.weight}
              floors={floor}
              elevator={elevator}
              onElevatorChange={setElevator}
              stairsRate={order.stairs_rate}
              elevatorRate={order.elevator_rate}
            />
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-nexolog-teal hover:bg-nexolog-teal/90 disabled:opacity-50 text-white font-bold py-3 rounded-xl"
          >
            {busy ? 'שומר...' : 'שלח עדכון'}
          </button>
        </form>
      </main>
    </div>
  );
}
